import React from 'react';
import { Book, Calendar, UserCheck, Wallet, Building2, AlertCircle } from 'lucide-react';
import SEO from '../Components/shared/SEO';

const TermsOfService = () => {
  return (
    <div className="max-w-4xl mx-auto md:p-6 bg-gray-50 min-h-screen">
      <SEO title="Terms of Service" path="/terms-of-service" description="The terms governing bookings, payments, and use of the Serenoly massage therapist marketplace." />
      <div className="bg-white rounded-lg shadow-sm p-8 mt-20 md:my-20">
        <div className="flex items-center justify-center mb-8">
          <Book className="h-8 w-8 text-primary mr-3" />
          <h1 className="text-3xl font-bold">Terms of Service</h1>
        </div>

        <div className="space-y-8">
          <section>
            <p className="text-gray-600">
              These Terms of Service govern your use of Serenoly, a marketplace platform that connects
              clients with independent, verified massage therapists and partner venues. By creating an
              account or booking through Serenoly, you agree to these terms.
            </p>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Bookings & Matching</h2>
            </div>
            <div className="text-gray-600 space-y-4">
              <p>By booking a massage through Serenoly, you agree to the following:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Bookings must be made with accurate personal, location, and health/safety intake information</li>
                <li>Once you submit a booking, Serenoly's matching system will attempt to pair you with the best-available verified therapist; a booking is not guaranteed until a therapist accepts it</li>
                <li>Cancellations should be made as early as possible through your account; late cancellations after a therapist has been matched or has started travelling may not be fully refundable</li>
                <li>You must disclose any health conditions or medications that may affect your treatment</li>
                <li>Serenoly and its partner therapists reserve the right to refuse or end a session that violates these terms or poses a safety concern</li>
              </ul>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Payments, Escrow & Wallets</h2>
            </div>
            <div className="text-gray-600 space-y-4">
              <ul className="list-disc ml-6 space-y-2">
                <li>Payment is required at the time of booking and is processed securely through Paystack</li>
                <li>Payment is held in escrow and is only released to the therapist (and venue partner, where applicable) once both the client and therapist confirm the session is complete</li>
                <li>Therapist and venue earnings are credited to a platform wallet, split between a withdrawable balance and non-withdrawable top-up credit</li>
                <li>Withdrawals require a verified bank account and are processed Monday&ndash;Friday via Paystack Transfers or manual admin payout</li>
                <li>Massage fees are split 60/40 (therapist/platform); venue room fees are split 70/30 (venue/platform)</li>
              </ul>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <UserCheck className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Therapists</h2>
            </div>
            <div className="text-gray-600 space-y-4">
              <p>Independent massage therapists who join Serenoly agree to:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Submit accurate identity and certification documents for admin review before accepting bookings</li>
                <li>Maintain professional conduct and accurately represent their coverage zone and massage-type preferences</li>
                <li>Confirm session completion accurately to trigger escrow release</li>
                <li>Be subject to a quality-score-driven matching system based on client ratings, with a three-strike suspension policy for serious or repeated issues</li>
              </ul>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Venue Partners</h2>
            </div>
            <div className="text-gray-600 space-y-4">
              <p>Hotels, guesthouses, and spas that join Serenoly as room partners agree to:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Submit accurate business and identity information for admin verification</li>
                <li>Keep room listings and availability up to date and honor bookings made through the platform</li>
                <li>Receive their share of the venue fee (70%) via the platform wallet once a session is confirmed complete</li>
              </ul>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-semibold">Liability & Disclaimers</h2>
            </div>
            <div className="text-gray-600 space-y-4">
              <p>Important disclaimers:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Serenoly is a marketplace facilitating bookings between clients and independent therapists/venue partners; massage services are provided by the therapist, not by Serenoly directly</li>
                <li>Disputes about a session (quality, no-show, safety concerns, etc.) can be raised through our dispute resolution process</li>
                <li>Serenoly is not liable for personal items lost or damaged during a session</li>
                <li>Clients and therapists assume the ordinary risks associated with in-person massage services</li>
                <li>Serenoly reserves the right to modify services, pricing splits, or availability without prior notice</li>
                <li>Force majeure events beyond Serenoly's control may affect service availability</li>
                <li>This agreement is governed by the laws of the Federal Republic of Nigeria</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">Modifications to Terms</h2>
            <p className="text-gray-600">
              We reserve the right to modify these terms at any time. Continued use of Serenoly constitutes acceptance of the updated terms.
            </p>
          </section>

          <div className="text-sm text-gray-500 mt-8">
            Last updated: September 6, 2026
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;